import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SECTION_IDS, scrollToSection } from './sections';

interface ScrollNavigatorProps {
  activeIndex: number;
}

export default function ScrollNavigator({ activeIndex }: ScrollNavigatorProps) {
  const { t } = useTranslation();
  const isLast = activeIndex >= SECTION_IDS.length - 1;

  const goNext = useCallback(() => {
    if (isLast) {
      scrollToSection(SECTION_IDS[0]);
    } else {
      scrollToSection(SECTION_IDS[activeIndex + 1]);
    }
  }, [activeIndex, isLast]);

  return (
    <div className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 flex-col items-center gap-3">
      <div className="flex gap-1.5 rounded-full border border-border bg-card/80 px-2.5 py-1.5 shadow-sm backdrop-blur-sm">
        {SECTION_IDS.map((id, i) => (
          <button
            key={id}
            onClick={() => scrollToSection(id)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === activeIndex
                ? 'w-6 bg-primary'
                : 'w-1.5 bg-text-secondary/30 hover:bg-text-secondary/60'
            }`}
            aria-label={t('landing.goToSection', { index: i + 1 })}
          />
        ))}
      </div>

      <button
        onClick={goNext}
        className={`flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/80 shadow-sm backdrop-blur-sm transition-all hover:bg-muted hover:shadow-md ${
          isLast ? 'rotate-180' : 'animate-bounce'
        }`}
        aria-label={isLast ? t('landing.scrollBack') : t('landing.scrollNext')}
      >
        <ChevronDown className="h-5 w-5 text-muted-foreground" />
      </button>
    </div>
  );
}
